import { FC, useMemo } from 'react'
import Head from 'next/head'
import Image from 'next/image'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import styles from './Encyclopedia.module.scss'
import CategoryCard from 'components/encyclopedia/category-card/CategoryCard'
import { Article, EncyclopediaProps } from './types'
import ArticleSection from 'features/encyclopedia/components/article-section/ArticleSection'
import HighlightedArticle from 'components/encyclopedia/highlighted-article/HighlightedArticle'
import SearchBar from 'components/encyclopedia/search-bar/SearchBar'
import { TITLE_POSTFIX } from 'constants/seo'

const Encyclopedia: FC<EncyclopediaProps> = ({ categories, articles, content }) => {
  // ROUTER
  const router = useRouter()
  const { locale: language } = router

  // Constants
  const highlightedArticle: Article = useMemo(() => articles.find((article) => article?.isHighlightedLanding), [articles])

  return (
    <>
      {/* SEO */}
      <Head>
        <title>
          {content?.seo?.seoTitle ?? content?.title} {TITLE_POSTFIX}
        </title>
        <meta property="og:title" content={content?.seo?.seoTitle ?? content?.title} key="title" />
        <meta name="description" content={content?.seo?.metaDescription ?? content?.description} />
      </Head>

      {/* CATEGORIES */}
      <nav className={classNames('container', styles.categories)}>
        {!!categories?.length && categories.map((cat) => <CategoryCard key={cat.id} category={cat} language={language} />)}
      </nav>

      {/* INTRO */}
      <section className={classNames('section is-dark', styles.intro)}>
        <div className="container">
          <div className={styles.content}>
            <h1>{content?.title}</h1>
            <p>{content?.description}</p>
            <h2>{content?.searchTitle}</h2>
            <SearchBar items={articles} placeholder={content?.searchPlaceholder} className={styles.search} />
          </div>
        </div>
        <figure className={styles['cover-img']}>
          {content?.coverImage?.url && (
            <Image layout="fill" src={content.coverImage.url} objectFit="cover" alt={content.coverImage?.alternativeText} />
          )}
        </figure>
      </section>

      {/* HIGHLIGHTED */}
      {highlightedArticle && <HighlightedArticle article={highlightedArticle} />}

      {/* POPULAR */}
      <ArticleSection
        randomize
        limit={4}
        articles={articles}
        title={content?.popularTitle}
        filter={(article) => article.isPopular}
        className={styles.popular}
      />
    </>
  )
}

export default Encyclopedia
